/**
 * Route guards.
 *
 * Onboarding state decides where someone belongs, so a half-finished signup
 * always resumes in the right place — whether they log in fresh, reload, or
 * hit a bookmarked URL for a screen they have not unlocked yet.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loading } from "./Loading";
import { useAuth } from "../store/auth";
import { routeForContributor } from "../lib/routing";

export function RequireAuth({ children }: { children: ReactNode }) {
  const contributor = useAuth((state) => state.contributor);
  const initialising = useAuth((state) => state.initialising);
  const location = useLocation();

  if (initialising) return <Loading />;
  if (!contributor) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** Screens that need onboarding finished — recording, earnings, payouts. */
export function RequireOnboarded({ children }: { children: ReactNode }) {
  const contributor = useAuth((state) => state.contributor);
  const initialising = useAuth((state) => state.initialising);

  if (initialising) return <Loading />;
  if (!contributor) return <Navigate to="/login" replace />;
  if (!contributor.onboarding?.completed) {
    return <Navigate to={routeForContributor(contributor)} replace />;
  }
  return <>{children}</>;
}

/** Signup and login: send anyone already signed in where they belong. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const contributor = useAuth((state) => state.contributor);
  const initialising = useAuth((state) => state.initialising);

  if (initialising) return <Loading />;
  if (contributor) return <Navigate to={routeForContributor(contributor)} replace />;
  return <>{children}</>;
}
